import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { negotiationAPI } from "@/lib/api";
import { toast } from "sonner";
import { 
  Loader2, 
  MessageSquare, 
  IndianRupee,
  ArrowRight,
  Package,
  Plane
} from "lucide-react";

/**
 * Dialog for making a price offer on a listing
 */
export function MakeOfferDialog({ 
  open, 
  onOpenChange, 
  listing, 
  listingType, // "cargo" or "jet"
  onSuccess 
}) {
  const [offeredPrice, setOfferedPrice] = useState("");
  const [message, setMessage] = useState("");
  const [quantity, setQuantity] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const originalPrice = listingType === "cargo" 
    ? listing?.price_per_kg 
    : listing?.price_per_seat;
  
  const priceUnit = listingType === "cargo" ? "per kg" : "per seat";
  const quantityUnit = listingType === "cargo" ? "kg" : "seats";
  const maxQuantity = listingType === "cargo" 
    ? listing?.available_capacity 
    : listing?.available_seats;

  const handleSubmit = async () => {
    const price = parseFloat(offeredPrice);
    const qty = parseFloat(quantity);

    if (isNaN(price) || price <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    if (price > originalPrice) {
      toast.error("Your offer should be less than the listed price");
      return;
    }

    if (isNaN(qty) || qty <= 0) {
      toast.error(`Please enter the ${quantityUnit} you need`);
      return;
    }

    if (qty > maxQuantity) {
      toast.error(`Maximum available: ${maxQuantity} ${quantityUnit}`);
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        listing_id: listing.id,
        listing_type: listingType,
        offered_price: price,
        message: message || undefined,
        ...(listingType === "cargo" ? { space_required: qty } : { seats_required: Math.floor(qty) })
      };

      await negotiationAPI.create(data);
      toast.success("Your offer has been sent!");
      onOpenChange(false);
      setOfferedPrice("");
      setMessage("");
      setQuantity("");
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit offer");
    } finally {
      setSubmitting(false);
    }
  };

  const savings = offeredPrice && originalPrice 
    ? ((originalPrice - parseFloat(offeredPrice)) / originalPrice * 100).toFixed(0)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {listingType === "cargo" ? (
              <Package className="w-5 h-5 text-orange-500" />
            ) : (
              <Plane className="w-5 h-5 text-orange-500" />
            )}
            Make an Offer
          </DialogTitle>
          <DialogDescription>
            Negotiate a better price with the {listingType === "cargo" ? "logistics partner" : "jet owner"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {/* Listing Info */}
          <div className="bg-muted/50 p-3 rounded-none border border-border">
            <p className="font-medium text-sm">{listing?.origin} → {listing?.destination}</p>
            <p className="text-sm text-muted-foreground mt-1">
              Listed price: <span className="font-bold text-foreground">₹{originalPrice}</span> {priceUnit}
            </p>
            <p className="text-xs text-muted-foreground">
              Available: {maxQuantity} {quantityUnit}
            </p>
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label>{listingType === "cargo" ? "Space Required (kg)" : "Seats Required"}</Label>
            <Input
              type="number"
              placeholder={`Enter ${quantityUnit} needed`}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="rounded-none"
              min="1"
              max={maxQuantity}
              data-testid="offer-quantity"
            />
          </div>

          {/* Your Offer */}
          <div className="space-y-2">
            <Label>Your Offer ({priceUnit})</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
              <Input
                type="number"
                placeholder={`Less than ${originalPrice}`}
                value={offeredPrice}
                onChange={(e) => setOfferedPrice(e.target.value)}
                className="pl-8 rounded-none"
                min="1"
                max={originalPrice}
                data-testid="offer-price"
              />
            </div>
            {savings > 0 && (
              <p className="text-xs text-green-600">
                You're asking for {savings}% discount
              </p>
            )}
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label>Message (Optional)</Label>
            <Textarea
              placeholder="Explain why you're making this offer..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="rounded-none resize-none"
              rows={2}
              data-testid="offer-message"
            />
          </div>

          {/* Total Estimate */}
          {offeredPrice && quantity && (
            <div className="bg-orange-50 border border-orange-200 p-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Estimated Total:</span>
                <span className="font-bold text-lg">
                  ₹{(parseFloat(offeredPrice) * parseFloat(quantity)).toLocaleString()}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {quantity} {quantityUnit} × ₹{offeredPrice} {priceUnit}
              </p>
            </div>
          )}

          {/* Submit */}
          <Button
            className="w-full rounded-none bg-orange-500 hover:bg-orange-600"
            onClick={handleSubmit}
            disabled={submitting || !offeredPrice || !quantity}
            data-testid="submit-offer-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <MessageSquare className="w-4 h-4 mr-2" />
            )}
            Send Offer
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            The partner will be notified and can accept, counter, or decline
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
